import { useEffect, useCallback, useMemo } from 'react';
import { ViewportGrid, usePrint, useViewportGrid } from '@ohif/ui';
import { ServicesManager, utils, Types } from '@ohif/core';
import { ViewportPane, ReportViewport } from '@vindr/ui';
import EmptyViewport from './EmptyViewport';
import classNames from 'classnames';
import SliceSelection from '../../../../extensions/cornerstone/src/utils/SliceSelection';
import {
  triggerEvent,
  eventTarget,
  cache as cs3DCache,
} from '@cornerstonejs/core';
import { isMobile } from '@vindr/utils/mobile';

const CHANGE_SERIES_EVENT = 'CHANGE_SERIES_EVENT';
const CHANGE_VIEW_MODE_EVENT = 'CHANGE_VIEW_MODE_EVENT';
const VOLUME_LOADER_SCHEME = 'cornerstoneStreamingImageVolume';

const { isEqualWithin } = utils;

const ORIENTATION_MAP = {
  axial: {
    viewPlaneNormal: [0, 0, -1],
    viewUp: [0, -1, 0],
  },
  sagittal: {
    viewPlaneNormal: [1, 0, 0],
    viewUp: [0, 0, 1],
  },
  coronal: {
    viewPlaneNormal: [0, 1, 0],
    viewUp: [0, 0, 1],
  },
};

function ViewerViewportGrid(props: ViewerViewportGridInterface) {
  const { servicesManager, viewportComponents, dataSource, isShowReportBtn } =
    props;
  const [viewportGrid, viewportGridService] = useViewportGrid();
  const [{ isPrintEnabled }] = usePrint();

  const {
    layout,
    activeViewportId,
    viewports,
    viewportType,
    studyList = [],
    isHangingProtocolLayout,
  } = viewportGrid;

  const { numCols, numRows } = layout || {};

  // TODO -> Need some way of selecting which displaySets hit the viewports.
  const {
    displaySetService,
    measurementService,
    hangingProtocolService,
    uiNotificationService,
    uiModalService,
    userAuthenticationService,
    httpClientService,
  } = (servicesManager as ServicesManager).services;

  /**
   * This callback runs after the viewports structure has changed in any way.
   * On initial display, that means if it has changed by applying a HangingProtocol,
   * while subsequently it may mean by changing the stage or by manually adjusting
   * the layout.

   */
  const updateDisplaySetsFromProtocol = (
    protocol: Types.HangingProtocol.Protocol,
    stage,
    activeStudyUID,
    viewportMatchDetails
  ) => {
    const availableDisplaySets = displaySetService.getActiveDisplaySets();

    if (!availableDisplaySets.length) {
      console.log('No available display sets', availableDisplaySets);
      return;
    }

    // Match each viewport individually
    const { layoutType } = stage.viewportStructure;
    const stageProps = stage.viewportStructure.properties;
    const { columns: numCols, rows: numRows, layoutOptions = [] } = stageProps;

    /**
     * This find or create viewport uses the hanging protocol results to
     * specify the viewport match details, which specifies the size and
     * setup of the various viewports.
     */
    const findOrCreateViewport = pos => {
      const viewportId = Array.from(viewportMatchDetails.keys())[pos];
      const details = viewportMatchDetails.get(viewportId);
      if (!details) {
        console.log('No match details for viewport', viewportId);
        return;
      }

      const { displaySetsInfo, viewportOptions } = details;
      const displaySetUIDsToHang = [];
      const displaySetUIDsToHangOptions = [];

      displaySetsInfo.forEach(
        ({ displaySetInstanceUID, displaySetOptions }) => {
          if (displaySetInstanceUID) {
            displaySetUIDsToHang.push(displaySetInstanceUID);
          }

          displaySetUIDsToHangOptions.push(displaySetOptions);
        }
      );

      const computedViewportOptions = hangingProtocolService.getComputedOptions(
        viewportOptions,
        displaySetUIDsToHang
      );

      const computedDisplaySetOptions =
        hangingProtocolService.getComputedOptions(
          displaySetUIDsToHangOptions,
          displaySetUIDsToHang
        );

      return {
        displaySetInstanceUIDs: displaySetUIDsToHang,
        displaySetOptions: computedDisplaySetOptions,
        viewportOptions: computedViewportOptions,
      };
    };

    viewportGridService.setLayout({
      numRows,
      numCols,
      layoutType,
      layoutOptions,
      findOrCreateViewport,
      isHangingProtocolLayout: true,
    });
  };

  const _getUpdatedViewports = useCallback(
    (viewportId, displaySetInstanceUID) => {
      let updatedViewports = [];
      try {
        const currentProtocol = hangingProtocolService.getActiveProtocol();
        const useHangingProtocolLayout =
          currentProtocol?.protocol?.id === 'mpr';
        updatedViewports = hangingProtocolService.getViewportsRequireUpdate(
          viewportId,
          displaySetInstanceUID,
          isHangingProtocolLayout
        );
      } catch (error) {
        console.warn(error);
        uiNotificationService.show({
          title: 'Drag and Drop',
          message:
            'The selected display sets could not be added to the viewport due to a mismatch in the Hanging Protocol rules.',
          type: 'info',
          duration: 3000,
        });
      }

      return updatedViewports;
    },
    [hangingProtocolService, uiNotificationService, isHangingProtocolLayout]
  );

  useEffect(() => {
    triggerEvent(eventTarget, CHANGE_VIEW_MODE_EVENT, viewportType);
  }, [viewportType]);

  // Using Hanging protocol engine to match the displaySets
  useEffect(() => {
    const { unsubscribe } = hangingProtocolService.subscribe(
      hangingProtocolService.EVENTS.PROTOCOL_CHANGED,
      ({ protocol, stage, activeStudyUID, viewportMatchDetails }) => {
        updateDisplaySetsFromProtocol(
          protocol,
          stage,
          activeStudyUID,
          viewportMatchDetails
        );
      }
    );

    return () => {
      unsubscribe();
    };
  }, [viewports]);

  useEffect(() => {
    const { unsubscribe } = measurementService.subscribe(
      measurementService.EVENTS.JUMP_TO_MEASUREMENT,
      ({ viewportId, measurement }) => {
        const {
          displaySetInstanceUID: referencedDisplaySetInstanceUID,
          metadata: { viewPlaneNormal },
        } = measurement;

        // if we already have the displaySet in one of the viewports
        // Todo: handle fusion display sets?
        for (const viewport of Array.from(viewports.values())) {
          const isMatch = viewport.displaySetInstanceUIDs.includes(
            referencedDisplaySetInstanceUID
          );
          if (isMatch) {
            return;
          }
        }

        const displaySet = displaySetService.getDisplaySetByUID(
          referencedDisplaySetInstanceUID
        );

        let imageIndex;
        // jump straight to the initial image index if we can
        if (displaySet.images && measurement.SOPInstanceUID) {
          imageIndex = displaySet.images.findIndex(
            image => image.SOPInstanceUID === measurement.SOPInstanceUID
          );
        }

        const updatedViewports = _getUpdatedViewports(
          viewportId,
          referencedDisplaySetInstanceUID
        );

        if (!updatedViewports || !updatedViewports.length) {
          return;
        }

        updatedViewports.forEach(vp => {
          const { orientation, viewportType } = vp.viewportOptions || {};
          let initialImageOptions;

          // For initial imageIndex to hang be careful for the volume viewport
          if (viewportType === 'volume') {
            // For the volume viewports, be careful to not jump in the viewports
            // that are not in the same orientation.
            // Todo: this doesn't work for viewports that have custom orientation
            // vectors specified
            if (
              orientation &&
              viewPlaneNormal &&
              isEqualWithin(
                ORIENTATION_MAP[orientation]?.viewPlaneNormal,
                viewPlaneNormal
              )
            ) {
              initialImageOptions = {
                index: imageIndex,
              };
            }
          } else if (viewportType === 'stack') {
            initialImageOptions = {
              index: imageIndex,
            };
          }

          if (initialImageOptions)
            vp.viewportOptions['initialImageOptions'] = initialImageOptions;
        });

        viewportGridService.setDisplaySetsForViewports(updatedViewports);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [viewports]);

  const onDropHandler = (viewportId, { displaySetInstanceUID }) => {
    const updatedViewports = _getUpdatedViewports(
      viewportId,
      displaySetInstanceUID
    );
    const displayset = displaySetService.getDisplaySetByUID(
      displaySetInstanceUID
    );

    const viewportType = updatedViewports?.[0]?.viewportOptions?.toolGroupId;

    if (viewportType && viewportType !== 'default') {
      const onClose = () => {
        uiModalService.hide();
      };

      const { activeViewportId, viewports } = viewportGridService.getState();
      const referencedDisplaySet = displaySetService.getDisplaySetByUID(
        viewports.get(activeViewportId).displaySetInstanceUIDs[0]
      );

      if (
        displayset.displaySetInstanceUID ===
        referencedDisplaySet.displaySetInstanceUID
      ) {
        uiNotificationService.show({
          title: 'Drag and Drop',
          message: 'The display set is currently selected',
          type: 'info',
          duration: 3000,
        });
        return;
      }

      const volumeLoaderSchema =
        referencedDisplaySet.volumeLoaderSchema ?? VOLUME_LOADER_SCHEME;
      const volumeId = `${volumeLoaderSchema}:${referencedDisplaySet.displaySetInstanceUID}`;

      uiModalService.show({
        content: SliceSelection,
        title: 'Select Slice',
        onClose: onClose,
        contentProps: {
          maxSize: displayset.numImageFrames,
          onClose: onClose,
          message: `${viewportType.toUpperCase()} might not work properly on low end device`,
          onSuccess: async value => {
            uiModalService.hide();

            triggerEvent(eventTarget, CHANGE_SERIES_EVENT, {
              displayset,
              updatedViewports,
              slices: value,
            });

            viewportGridService.setDisplaySetsForViewports(
              updatedViewports.map(it => ({
                ...it,
                viewportOptions: { ...it.viewportOptions, slices: value },
              }))
            );

            setTimeout(() => {
              cs3DCache.removeVolumeLoadObject(volumeId);
            }, 2000);
          },
        },
      });
    } else {
      triggerEvent(eventTarget, CHANGE_SERIES_EVENT, {
        displayset,
        updatedViewports,
      });

      viewportGridService.setDisplaySetsForViewports(updatedViewports);
    }
  };

  const isMPRMode = useMemo(() => {
    return viewportType === 'mpr' || viewportType === 'orthogonal';
  }, [viewportType]);

  const getViewportPatientInfo = (studies = [], displaySets = [], isActive) => {
    // StudyInstanceUID : for pdf viewport
    const { studyInstanceUid, StudyInstanceUID } = displaySets[0] || {};
    const foundStudy = studies.find(
      study =>
        study.studyInstanceUid === studyInstanceUid ||
        study.studyInstanceUid === StudyInstanceUID
    );
    if (!foundStudy) return null;
    const arrPatientData = [];
    const { patientName, patientId, patientSex, description, date } =
      foundStudy;
    patientName && arrPatientData.push(patientName);
    patientId && arrPatientData.push(patientId);
    patientSex && arrPatientData.push(patientSex);
    if (description && description !== '*') {
      arrPatientData.push(description);
    }
    if (date) {
      arrPatientData.push(date);
    }
    const patientInfo = arrPatientData.join(' | ');

    return (
      <div
        className={classNames(
          isActive ? 'bg-primary-400' : 'bg-[#333333]',
          'flex w-full items-center justify-between text-sm text-white'
        )}
      >
        <div className="flex flex-1 overflow-hidden whitespace-nowrap p-2">
          <div className="overflow-hidden text-ellipsis" title={patientInfo}>
            {patientInfo}
          </div>
        </div>
        {isShowReportBtn && (
          <div className="ml-2 pr-2">
            <ReportViewport
              userAuthenticationService={userAuthenticationService}
              httpClientService={httpClientService}
              isActive={isActive}
              study={foundStudy}
            />
          </div>
        )}
      </div>
    );
  };

  // handle for MPR mode
  const getActiveViewportPatientInfo = () => {
    const paneMetadata = viewports.get(activeViewportId) || {};
    const { displaySetInstanceUIDs = [] } = paneMetadata;
    const displaySets = displaySetInstanceUIDs.map(displaySetInstanceUID => {
      return displaySetService.getDisplaySetByUID(displaySetInstanceUID) || {};
    });

    if (!displaySets) return;
    return getViewportPatientInfo(studyList, displaySets, true);
  };

  const getViewportPanes = useCallback(() => {
    const viewportPanes = [];

    const numViewports = viewportGridService.getNumViewportPanes();
    for (let i = 0; i < numViewports; i++) {
      const paneMetadata = Array.from(viewports.values())[i] || {};
      const {
        displaySetInstanceUIDs,
        viewportOptions = {},
        displaySetOptions, // array of options for each display set in the viewport
        x: viewportX,
        y: viewportY,
        width: viewportWidth,
        height: viewportHeight,
        viewportLabel,
      } = paneMetadata;

      const viewportId = viewportOptions.viewportId;
      const isActive = activeViewportId === viewportId;
      const displaySetInstanceUIDsToUse = displaySetInstanceUIDs || [];

      const displaySets = displaySetInstanceUIDsToUse.map(
        displaySetInstanceUID => {
          return (
            displaySetService.getDisplaySetByUID(displaySetInstanceUID) || {}
          );
        }
      );

      const ViewportComponent = _getViewportComponent(
        displaySets,
        viewportComponents,
        uiNotificationService
      );

      // look inside displaySets to see if they need reRendering
      const displaySetsNeedsRerendering = displaySets.some(displaySet => {
        return displaySet.needsRerendering;
      });

      const onInteractionHandler = event => {
        if (isActive) return;

        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }

        viewportGridService.setActiveViewportId(viewportId);
      };

      viewportPanes[i] = (
        <ViewportPane
          key={viewportId}
          acceptDropsFor="displayset"
          onDrop={onDropHandler.bind(null, viewportId)}
          onInteraction={onInteractionHandler}
          customStyle={{
            position: 'absolute',
            top: viewportY * 100 + 0.1 + '%',
            left: viewportX * 100 + 0.1 + '%',
            width: viewportWidth * 100 - 0.2 + '%',
            height: viewportHeight * 100 - 0.2 + '%',
          }}
          isActive={isActive}
        >
          {!isMPRMode && !isMobile() && !isPrintEnabled && (
            <div className="viewport-patient-info h-[30px] w-full">
              {getViewportPatientInfo(studyList, displaySets, isActive)}
            </div>
          )}
          <div
            style={{
              height:
                !isMPRMode && !isMobile() && !isPrintEnabled
                  ? 'calc(100% - 30px)'
                  : '100%',
            }}
            className={classNames(
              'viewport-container flex w-full flex-1 flex-col',
              {
                'pointer-events-none': !isActive,
              }
            )}
          >
            <ViewportComponent
              displaySets={displaySets}
              viewportId={viewportId}
              viewportLabel={numViewports > 1 ? viewportLabel : ''}
              dataSource={dataSource}
              viewportOptions={viewportOptions}
              displaySetOptions={displaySetOptions}
              needsRerendering={displaySetsNeedsRerendering}
              isHangingProtocolLayout={isHangingProtocolLayout}
              onElementEnabled={evt => {
                viewportGridService.setViewportIsReady(viewportId, true);
              }}
            />
          </div>
        </ViewportPane>
      );
    }

    return viewportPanes;
  }, [
    viewports,
    activeViewportId,
    viewportComponents,
    dataSource,
    studyList,
    isMPRMode,
    isPrintEnabled,
  ]);

  /**
   * Loading indicator until numCols and numRows are gotten from the hangingProtocolService
   */
  if (!numRows || !numCols) {
    return null;
  }

  return (
    <ViewportGrid
      numRows={numRows}
      numCols={numCols}
      viewportType={viewportType}
      activePatientInfo={
        isMPRMode && !isMobile() ? getActiveViewportPatientInfo : ''
      }
    >
      {/* {ViewportPanes} */}
      {getViewportPanes()}
    </ViewportGrid>
  );
}

interface ViewerViewportGridInterface {
  servicesManager: ServicesManager;
  viewportComponents: any[];
  dataSource: any;
  isShowReportBtn?: boolean;
}

ViewerViewportGrid.defaultProps = {
  viewportComponents: [],
  isShowReportBtn: false,
};

function _getViewportComponent(
  displaySets,
  viewportComponents,
  uiNotificationService
) {
  if (!displaySets || !displaySets.length) {
    return EmptyViewport;
  }

  // Todo: Do we have a viewport that has two different SOPClassHandlerIds?
  const SOPClassHandlerId = displaySets[0].SOPClassHandlerId;

  for (let i = 0; i < viewportComponents.length; i++) {
    if (!viewportComponents[i]) {
      throw new Error('viewport components not defined');
    }
    if (!viewportComponents[i].displaySetsToDisplay) {
      throw new Error('displaySetsToDisplay is null');
    }
    if (
      viewportComponents[i].displaySetsToDisplay.includes(SOPClassHandlerId)
    ) {
      const { component } = viewportComponents[i];
      return component;
    }
  }

  console.log("Can't show displaySet", SOPClassHandlerId, displaySets[0]);
  uiNotificationService.show({
    title: 'Viewport Not Supported Yet',
    message: `Cannot display SOPClassId of ${displaySets[0].SOPClassUID} yet`,
    type: 'error',
  });

  return EmptyViewport;
}

export default ViewerViewportGrid;
